import React, { Component } from "react";
import { Link } from "react-router-dom";
import { connect } from 'react-redux';
import { Button, Table } from 'react-bootstrap';

import { ContainerWrapper, Content } from '../../components/GridComponent/AdminLte.js';
import PageLoading from "../PageLoading";
import Wrapper from "../../components/Template/Wrapper";
import Authenticator from "../../services/Authenticator";
import { fetchManagedBroadcasts, setBroadcastStatus, deleteBroadcast } from '../../store/actions/broadcasts/broadcastActions';
import "./Broadcasts.css";

/**
 * Announcements -> Manage. The items the caller may manage: their own, their department's,
 * or (HR only) everything. Row actions: Edit, Reach, Deactivate / Activate, Delete.
 * A form item the caller may view (server flag `can_view_responses`) also gets Responses; a
 * per-person form (`per_person`, SPEC 23.2) also gets Documents.
 */

const KIND_LABEL = { notice: "Notice", form: "Form", custom: "Custom" };
const MODE_LABEL = { popup: "Pop-up", tab: "Tab" };

// Reach column. List rows carry {shown, completed, audience: null} (BroadcastPresenter::reachCounts —
// the audience is only computed on the Reach page); the audience is appended as "of <n>" only when
// the server sent a number. House choice: "<shown> shown / <completed> completed[ of <audience>]".
export const reachText = (reach) => {
  const r = reach || {};
  const base = Number(r.shown || 0) + " shown / " + Number(r.completed || 0) + " completed";
  return r.audience === null || r.audience === undefined ? base : base + " of " + Number(r.audience);
};

const SCOPES = [
  { key: "mine", label: "Mine" },
  { key: "department", label: "My department" },
  { key: "all", label: "All", hrOnly: true },
];

class BroadcastList extends Component {

  state = {
    scope: "mine",
  };

  componentDidMount() {
    this.props.fetchManagedBroadcasts(this.state.scope);
  }

  selectScope = (scope) => {
    this.setState({ scope: scope });
    this.props.fetchManagedBroadcasts(scope);
  }

  onToggleStatus = (item) => {
    this.props.setBroadcastStatus(item.id, item.status == 1 ? 0 : 1);
  }

  onDelete = (item) => {
    if (window.confirm('Delete "' + item.title + '"? Employees will no longer see it.')) {
      this.props.deleteBroadcast(item.id);
    }
  }

  renderRow = (item) => {
    const modes = Array.isArray(item.display_mode) ? item.display_mode : String(item.display_mode || "").split(",");
    return (
      <tr key={item.id}>
        <td>{item.title}</td>
        <td>{KIND_LABEL[item.kind] || item.kind}</td>
        <td>{modes.filter(Boolean).map((m) => MODE_LABEL[m] || m).join(", ")}</td>
        <td className="broadcast-window">
          {item.starts_at_local} &rarr; {item.ends_at_local}
          <br /><small>{item.timezone}</small>
        </td>
        <td>{item.behaviour === "mandatory" ? "Mandatory" : "Closable"}</td>
        <td>{item.status == 1 ? "Active" : "Inactive"}</td>
        <td>{item.audience_summary}</td>
        <td>{item.creator_name}</td>
        <td>{reachText(item.reach)}</td>
        <td className="broadcast-actions">
          <Link className="btn btn-sm btn-outline-primary" to={global.links.broadcast_item + item.id + "/edit"}>Edit</Link>
          {item.kind === "form" && item.can_view_responses ? (
            // Only where the server says the caller is a responses viewer (SPEC 8.1); no dead links.
            <Link className="btn btn-sm btn-outline-secondary" to={global.links.broadcast_item + item.id + "/responses"}>Responses</Link>
          ) : null}
          {item.kind === "form" && Number(item.per_person || 0) ? (
            // SPEC 23.8: a per-person form's documents (raise / track / withdraw / export).
            <Link className="btn btn-sm btn-outline-secondary" to={global.links.broadcast_item + item.id + "/documents"}>Documents</Link>
          ) : null}
          <Link className="btn btn-sm btn-outline-secondary" to={global.links.broadcast_item + item.id + "/reach"}>Reach</Link>
          <Button size="sm" variant="outline-warning" onClick={() => this.onToggleStatus(item)}>
            {item.status == 1 ? "Deactivate" : "Activate"}
          </Button>
          <Button size="sm" variant="outline-danger" onClick={() => this.onDelete(item)}>Delete</Button>
        </td>
      </tr>
    );
  }

  renderBody = () => {
    const { manage } = this.props;
    if (!manage.isLoaded) {
      return manage.loadError ? <p>Couldn't load announcements right now.</p> : <PageLoading />;
    }
    if (manage.list.length === 0) {
      return <p>No announcements yet.</p>;
    }
    return (
      <Table responsive hover className="broadcast-table">
        <thead>
          <tr>
            <th>Title</th><th>Kind</th><th>Display</th><th>Window</th><th>Behaviour</th>
            <th>Status</th><th>Audience</th><th>Created by</th><th>Reach</th><th></th>
          </tr>
        </thead>
        <tbody>
          {manage.list.map(this.renderRow)}
        </tbody>
      </Table>
    );
  }

  render() {
    const isHr = Authenticator.scanLevel("HR");

    return <Wrapper {...this.props} >
      <ContainerWrapper>
        <Content col="12" title="Manage announcements">
          <div className="broadcast-toolbar">
            <div className="btn-group broadcast-scopes" role="group">
              {SCOPES.filter((s) => !s.hrOnly || isHr).map((s) => (
                <Button
                  key={s.key}
                  variant={this.state.scope === s.key ? "primary" : "outline-primary"}
                  onClick={() => this.selectScope(s.key)}
                >
                  {s.label}
                </Button>
              ))}
            </div>
            <Link className="btn btn-success" to={global.links.broadcast_new}>Post</Link>
          </div>
          {this.renderBody()}
        </Content>
      </ContainerWrapper>
    </Wrapper>;
  }
}

const mapStateToProps = (state) => {
  return {
    user    : state.user,
    manage  : state.broadcasts.manage,
  }
}
const mapDispatchToProps = (dispatch) => {
  return {
    fetchManagedBroadcasts  : ( scope ) => dispatch( fetchManagedBroadcasts( scope ) ),
    setBroadcastStatus      : ( id, status ) => dispatch( setBroadcastStatus( id, status ) ),
    deleteBroadcast         : ( id ) => dispatch( deleteBroadcast( id ) ),
  }
}
export default connect(mapStateToProps, mapDispatchToProps)(BroadcastList);
